import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';
import { thb, shortDate, relTime } from '../money.js';
import { Segmented, Empty, Spinner, Avatar, EntityGlyph, Chip, Overlay } from '../ui.jsx';

/**
 * Cold Call.
 *
 * Three tabs, three states of the same motion: sourced → being worked → dead.
 *
 * The design's button says "Generate a list". It isn't one, and pretending
 * otherwise would be the worst kind of demo-ware — LinkedIn Sales Navigator has
 * no prospecting API and scraping it breaches ToS, and event lists arrive as
 * spreadsheets. So the control is labelled Import, says why, and the sources
 * that genuinely can be pulled live (the DBD registry, ad activity) light up
 * here when B5 wires them in.
 */
export default function ColdCall({ me, config, onLogCall, onOpenClient, toast }) {
  const { t, lang } = useLang();
  const [tab, setTab] = useState('prospects');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);

  const load = useCallback(() => {
    setData(null);
    setError('');
    api
      .prospects({ tab })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast(okMsg);
      load();
    } catch (e) {
      toast(e.message);
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '18px 24px 6px',
        }}
      >
        <div style={{ marginRight: 'auto' }}>
          <div className="display" style={{ fontSize: 22 }}>
            {t('navCalls')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>{t('coldBlurb')}</div>
        </div>
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: 'prospects', label: t('tabProspects') },
            { value: 'queue', label: t('tabQueue') },
            { value: 'cold', label: t('tabCold') },
          ]}
        />
        <button className="btn btn-primary" onClick={() => setImporting(true)}>
          {t('importList')}
        </button>
      </div>

      {error && <div className="page">{error}</div>}
      {!error && !data && <Spinner label={t('loading')} />}

      {data && tab === 'prospects' && (
        <div style={{ padding: '8px 24px 24px' }}>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
            {t('excludesExisting')}
          </div>
          {data.prospects.length === 0 ? (
            <Empty>{t('noProspects')}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('company')}</th>
                    <th>{t('industry')}</th>
                    <th style={{ width: 110 }}>{t('fit')}</th>
                    <th>{t('whyNow')}</th>
                    <th>{t('contactFound')}</th>
                    <th style={{ textAlign: 'right' }}>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.prospects.map((p) => (
                    <tr key={p.id}>
                      <td style={{ whiteSpace: 'normal', minWidth: 200 }}>
                        <span
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 600 }}
                        >
                          {p.entity_tag && <EntityGlyph entity={p.entity_tag} />}
                          {p.raw_name}
                        </span>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{p.source}</div>
                      </td>
                      <td>{p.industry || '—'}</td>
                      <td>
                        <FitBar score={p.fit_score} />
                      </td>
                      <td style={{ whiteSpace: 'normal', maxWidth: 260, fontSize: 12.5 }}>
                        {p.signal || <span style={{ color: 'var(--ink-3)' }}>—</span>}
                      </td>
                      <td>
                        {p.contact_name ? (
                          <>
                            <div>{p.contact_name}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{p.phone}</div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--ink-3)' }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => act(() => api.queueProspect(p.id), t('addToQueue'))}
                        >
                          {t('addToQueue')}
                        </button>{' '}
                        <button
                          className="btn btn-sm"
                          onClick={() => act(() => api.dismissProspect(p.id, 'Not a fit'), t('notAFit'))}
                        >
                          {t('notAFit')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {data && tab === 'queue' && (
        <div style={{ padding: '8px 24px 24px' }}>
          {data.prospects.length === 0 ? (
            <Empty>{t('noQueue')}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('company')}</th>
                    <th style={{ textAlign: 'right' }}>{t('attempts')}</th>
                    <th>{t('contact')}</th>
                    <th>{t('lastOutcome')}</th>
                    <th>{t('nextCall')}</th>
                    <th style={{ textAlign: 'right' }}>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.prospects.map((p) => (
                    <tr key={p.id}>
                      <td style={{ whiteSpace: 'normal', minWidth: 200, fontWeight: 600 }}>
                        {p.raw_name}
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {p.attempts}
                      </td>
                      <td>
                        {p.contact_name || '—'}
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{p.phone}</div>
                      </td>
                      <td>
                        {p.last_outcome ? (
                          <span className="pill">{p.last_outcome}</span>
                        ) : (
                          <span style={{ color: 'var(--ink-3)' }}>—</span>
                        )}
                      </td>
                      <td
                        style={{
                          color:
                            p.next_call_at && p.next_call_at <= new Date().toISOString().slice(0, 10)
                              ? 'var(--warning)'
                              : 'var(--ink-2)',
                          fontWeight: 600,
                        }}
                      >
                        {shortDate(p.next_call_at, lang)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() =>
                            onLogCall({
                              prospect_id: p.id,
                              label: p.raw_name,
                              attempts: p.attempts,
                              contact_name: p.contact_name,
                              phone: p.phone,
                              entity_tag: p.entity_tag,
                            })
                          }
                        >
                          {t('logThisCall')}
                        </button>{' '}
                        <button
                          className="btn btn-sm"
                          onClick={() =>
                            act(() => api.convertProspect(p.id), t('onboardToLead'))
                          }
                        >
                          {t('onboardToLead')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {data && tab === 'cold' && (
        <div style={{ padding: '8px 24px 24px' }}>
          {data.cold.length === 0 ? (
            <Empty>{t('noCold')}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('company')}</th>
                    <th>{t('deal')}</th>
                    <th>{t('owner')}</th>
                    <th>{t('wentCold')}</th>
                    <th>{t('lastStage')}</th>
                    <th>{t('reason')}</th>
                    <th style={{ textAlign: 'right' }}>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cold.map((r) => (
                    <tr key={r.deal_id}>
                      <td
                        style={{ whiteSpace: 'normal', minWidth: 180, fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => onOpenClient(r.company_id)}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                          <EntityGlyph entity={r.entity_tag} />
                          {r.company_name}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'normal', maxWidth: 200 }}>
                        {r.title}
                        <div className="num" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                          {thb(r.amount_satang)}
                        </div>
                      </td>
                      <td>{r.owner_name || '—'}</td>
                      <td style={{ color: 'var(--ink-2)' }}>
                        {r.cold_days}d · {relTime(r.closed_at || r.last_activity_at, lang)}
                      </td>
                      <td>
                        <span className="pill">{r.stage}</span>
                      </td>
                      <td style={{ whiteSpace: 'normal', maxWidth: 220, fontSize: 12.5 }}>
                        {r.loss_reason || <span style={{ color: 'var(--ink-3)' }}>went quiet</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() =>
                            act(() => api.reviveProspect({ company_id: r.company_id }), t('reopen'))
                          }
                        >
                          {t('reopen')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {importing && (
        <ImportModal
          me={me}
          config={config}
          onClose={() => setImporting(false)}
          onDone={(res) => {
            setImporting(false);
            toast(t('added', { n: res.added, s: res.skipped }));
            setTab('prospects');
            load();
          }}
        />
      )}
    </>
  );
}

/** Score plus a bar — a number alone doesn't tell you how it compares. */
function FitBar({ score }) {
  const s = score ?? 0;
  const color = s >= 65 ? 'var(--success)' : s >= 35 ? 'var(--warning)' : 'var(--ink-3)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <span
        style={{
          width: 46,
          height: 6,
          borderRadius: 3,
          background: 'var(--sunken)',
          overflow: 'hidden',
          display: 'inline-block',
        }}
      >
        <span style={{ display: 'block', width: `${s}%`, height: '100%', background: color }} />
      </span>
      <span className="num" style={{ fontWeight: 700, color, fontSize: 12 }}>
        {s}
      </span>
    </span>
  );
}

/**
 * Import. Accepts pasted lines or CSV, because that is how these lists actually
 * arrive — as an export from Sales Navigator, a delegate list from an event, or
 * a name someone wrote down.
 */
function ImportModal({ me, config, onClose, onDone }) {
  const { t } = useLang();
  const [source, setSource] = useState('linkedin');
  const [entity, setEntity] = useState(me.entities[0] || 'BTM');
  const [industries, setIndustries] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const parse = (raw) =>
    raw
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        const [raw_name, industry, phone, contact_name] = line.split(',').map((s) => s?.trim());
        return {
          raw_name,
          industry: industry || undefined,
          phone: phone || undefined,
          contact_name: contact_name || undefined,
        };
      })
      .filter((r) => r.raw_name);

  const submit = async (e) => {
    e.preventDefault();
    const rows = parse(text);
    if (!rows.length) {
      setError('Nothing to import.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await api.importProspects({
        source,
        entity_tag: entity,
        target_industries: industries
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        rows,
      });
      onDone(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose} kind="modal">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '18px 20px 10px' }}>
          <div className="display" style={{ fontSize: 18 }}>
            {t('importList')}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 4, lineHeight: 1.5 }}>
            {t('importBlurb')}
          </div>
        </div>

        <div style={{ padding: '0 20px', overflow: 'auto' }}>
          <label className="field-label">{t('sources')}</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {(config.prospect_sources || []).map((s) => (
              <Chip key={s} on={source === s} onClick={() => setSource(s)}>
                {s}
              </Chip>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <div>
              <label className="field-label">{t('entity')}</label>
              <select className="select" value={entity} onChange={(e) => setEntity(e.target.value)}>
                {(me.entities.length ? me.entities : ['BTM', 'MR']).map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Target industries (comma separated)</label>
              <input
                className="input"
                value={industries}
                onChange={(e) => setIndustries(e.target.value)}
                placeholder="Beverage, Retail, Automotive"
              />
            </div>
          </div>

          <label className="field-label" style={{ marginTop: 12 }}>
            Companies — one per line
          </label>
          <textarea
            className="textarea"
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'Thai Beverage Retail Co, Beverage, +66 2 555 0000, Areeya S.\nNakara Group, Hospitality'}
            style={{ fontFamily: 'var(--f-mono)', fontSize: 12.5 }}
          />
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 6 }}>
            {parse(text).length} rows · {t('excludesExisting').toLowerCase()}
          </div>

          {error && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 10 }}>{error}</div>
          )}
        </div>

        <div className="drawer-foot" style={{ marginTop: 14 }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>
            {t('cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !parse(text).length}>
            {t('generate')}
          </button>
        </div>
      </form>
    </Overlay>
  );
}
